import React from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { hasFullHelpDocument, loadHelpDocument } from '../help-documents.js';
import './help-reader-page.scss';

function HelpReaderPage() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [markdown, setMarkdown] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(true);
  const [isFallback, setIsFallback] = React.useState(false);
  const canLoadFullGuide = React.useMemo(
    () => hasFullHelpDocument(slug),
    [slug],
  );

  React.useEffect(() => {
    let cancelled = false;

    if (!canLoadFullGuide) {
      setMarkdown('');
      setIsFallback(false);
      setIsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setIsLoading(true);
    setIsFallback(false);

    loadHelpDocument({ locale: i18n.language, slug, variant: 'full' })
      .then((result) => {
        if (cancelled) return;
        setMarkdown(result.markdown);
        setIsFallback(result.isFallback);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [canLoadFullGuide, i18n.language, slug]);

  function handleBack() {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate('/dashboard');
  }

  return (
    <div className="help-reader-page">
      <header className="help-reader-header">
        <button type="button" className="btn btn-outline-secondary" onClick={handleBack}>
          <i className="ti ti-arrow-left me-1"></i>
          {t('common.back')}
        </button>
        <div className="help-reader-title-wrap">
          <div className="help-reader-kicker">{t('header.help')}</div>
          <h1 className="help-reader-title">{t('help.fullGuideTitle')}</h1>
        </div>
      </header>

      <main className="help-reader-content">
        <article className="help-reader-article">
          {isLoading && (
            <div className="help-reader-loading">
              <i className="ti ti-loader-2 me-2"></i>
              {t('common.loading')}
            </div>
          )}

          {!isLoading && isFallback && (
            <div className="help-reader-fallback-notice">
              {t('help.fallbackNotice')}
            </div>
          )}

          {!isLoading && !markdown && (
            <p className="help-reader-empty">{t('help.comingSoon')}</p>
          )}

          {!isLoading && markdown && (
            <div className="help-markdown-body help-markdown-body--reader">
              <Markdown remarkPlugins={[remarkGfm]}>{markdown}</Markdown>
            </div>
          )}
        </article>
      </main>
    </div>
  );
}

export default HelpReaderPage;
